import type { IconName } from "./icons.js";
import { isPmosFigmaPanelEnabled } from "./controllers/pmos-figma.ts";
import { isPmosLibreChatEnabled } from "./controllers/pmos-librechat.ts";

export type Tab =
  | "dashboard"
  | "automations"
  | "runs"
  | "integrations"
  | "figma"
  | "models"
  | "connections"
  | "command-center"
  | "admin"
  | "agents"
  | "overview"
  | "channels"
  | "instances"
  | "sessions"
  | "usage"
  | "cron"
  | "skills"
  | "nodes"
  | "chat"
  | "librechat"
  | "config"
  | "debug"
  | "logs";

export const TAB_GROUPS = [
  // Dedicated chat section at the top.
  {
    label: "Chat",
    tabs: (isPmosLibreChatEnabled() ? ["chat", "librechat"] : ["chat"]) as Tab[],
  },
  // Core Wicked OS workspace surface.
  {
    label: "Wicked OS",
    tabs: (isPmosFigmaPanelEnabled()
      ? ["dashboard", "integrations", "figma", "command-center"]
      : ["dashboard", "integrations", "command-center"]) as Tab[],
  },
  // Agent management.
  {
    label: "Agents",
    tabs: ["models", "agents", "skills", "nodes"],
  },
  // Runtime control surfaces.
  {
    label: "Control",
    tabs: ["overview", "channels", "instances", "sessions", "usage", "cron"],
  },
  // Low-level operational settings.
  {
    label: "Settings",
    tabs: [
      "config",
      "debug",
      "logs",
    ],
  },
] as const;

const TAB_PATHS: Record<Tab, string> = {
  dashboard: "/",
  automations: "/automations",
  runs: "/runs",
  integrations: "/integrations",
  figma: "/figma",
  models: "/models",
  connections: "/connections",
  "command-center": "/command-center",
  admin: "/admin",
  agents: "/agents",
  overview: "/overview",
  channels: "/channels",
  instances: "/instances",
  sessions: "/sessions",
  usage: "/usage",
  cron: "/cron",
  skills: "/skills",
  nodes: "/nodes",
  chat: "/chat",
  librechat: "/librechat",
  config: "/config",
  debug: "/debug",
  logs: "/logs",
};

const PATH_TO_TAB = new Map(Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab as Tab]));

export function normalizeBasePath(basePath: string): string {
  if (!basePath) {
    return "";
  }
  let base = basePath.trim();
  if (!base.startsWith("/")) {
    base = `/${base}`;
  }
  if (base === "/") {
    return "";
  }
  if (base.endsWith("/")) {
    base = base.slice(0, -1);
  }
  return base;
}

export function normalizePath(path: string): string {
  if (!path) {
    return "/";
  }
  let normalized = path.trim();
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}

export function pathForTab(tab: Tab, basePath = ""): string {
  const base = normalizeBasePath(basePath);
  const path = TAB_PATHS[tab];
  return base ? `${base}${path}` : path;
}

export function tabFromPath(pathname: string, basePath = ""): Tab | null {
  const base = normalizeBasePath(basePath);
  let path = pathname || "/";
  if (base) {
    if (path === base) {
      path = "/";
    } else if (path.startsWith(`${base}/`)) {
      path = path.slice(base.length);
    }
  }
  let normalized = normalizePath(path).toLowerCase();
  if (normalized.endsWith("/index.html")) {
    normalized = "/";
  }
  if (normalized === "/") {
    return "dashboard";
  }
  return PATH_TO_TAB.get(normalized) ?? null;
}

export function inferBasePathFromPathname(pathname: string): string {
  let normalized = normalizePath(pathname);
  if (normalized.endsWith("/index.html")) {
    normalized = normalizePath(normalized.slice(0, -"/index.html".length));
  }
  if (normalized === "/") {
    return "";
  }
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "";
  }
  for (let i = 0; i < segments.length; i++) {
    const candidate = `/${segments.slice(i).join("/")}`.toLowerCase();
    if (PATH_TO_TAB.has(candidate)) {
      const prefix = segments.slice(0, i);
      return prefix.length ? `/${prefix.join("/")}` : "";
    }
  }
  return `/${segments.join("/")}`;
}

export function iconForTab(tab: Tab): IconName {
  switch (tab) {
    case "dashboard":
      return "barChart";
    case "automations":
      return "zap";
    case "runs":
      return "scrollText";
    case "integrations":
      return "link";
    case "figma":
      return "figma";
    case "models":
      return "brain";
    case "command-center":
      return "zap";
    case "admin":
      return "settings";
    case "agents":
      return "folder";
    case "chat":
      return "messageSquare";
    case "librechat":
      return "globe";
    case "overview":
      return "barChart";
    case "channels":
      return "link";
    case "instances":
      return "radio";
    case "sessions":
      return "fileText";
    case "usage":
      return "barChart";
    case "cron":
      return "loader";
    case "skills":
      return "zap";
    case "nodes":
      return "monitor";
    case "config":
      return "settings";
    case "debug":
      return "bug";
    case "logs":
      return "scrollText";
    default:
      return "folder";
  }
}

export function titleForTab(tab: Tab) {
  switch (tab) {
    case "dashboard":
      return "Dashboard";
    case "automations":
      return "Workflows";
    case "runs":
      return "Runs";
    case "integrations":
      return "Integrations";
    case "figma":
      return "Figma";
    case "models":
      return "Models";
    case "connections":
      return "Connections";
    case "command-center":
      return "Projects";
    case "admin":
      return "Admin";
    case "agents":
      return "Agents";
    case "overview":
      return "Overview";
    case "channels":
      return "Channels";
    case "instances":
      return "Instances";
    case "sessions":
      return "Sessions";
    case "usage":
      return "Usage";
    case "cron":
      return "Cron Jobs";
    case "skills":
      return "Skills";
    case "nodes":
      return "Nodes";
    case "chat":
      return "Chat";
    case "librechat":
      return "LibreChat";
    case "config":
      return "Config";
    case "debug":
      return "Debug";
    case "logs":
      return "Logs";
    default:
      return "Control";
  }
}

export function subtitleForTab(tab: Tab) {
  switch (tab) {
    case "dashboard":
      return "Your automation OS at a glance: health, integrations, and what is running now.";
    case "automations":
      return "Build and run workflows with your personal workflow engine workspace.";
    case "runs":
      return "Live and historical executions across agents and automations.";
    case "integrations":
      return "Connect Flow, Basecamp, and provider keys for this workspace.";
    case "figma":
      return "Embed the Figma File Manager, sync active design context, and run design audits.";
    case "models":
      return "Create and manage workspace models, keys, and agent assignments.";
    case "connections":
      return "Manage native Flow connections for this workspace.";
    case "command-center":
      return "Basecamp project management center with live summaries and AI operations.";
    case "admin":
      return "Workspace identity, roles, members, and Wicked OS audit feed.";
    case "agents":
      return "Manage agent workspaces, tools, and identities.";
    case "overview":
      return "Gateway status, entry points, and a fast health read.";
    case "channels":
      return "Manage channels and settings.";
    case "instances":
      return "Presence beacons from connected clients and nodes.";
    case "sessions":
      return "Inspect active sessions and adjust per-session defaults.";
    case "usage":
      return "";
    case "cron":
      return "Schedule wakeups and recurring agent runs.";
    case "skills":
      return "Manage skill availability and API key injection.";
    case "nodes":
      return "Paired devices, capabilities, and command exposure.";
    case "chat":
      return "Direct gateway chat session for quick interventions.";
    case "librechat":
      return "PMOS bot chat powered by LibreChat, with sessions grouped by bot and richer file support.";
    case "config":
      return "Edit ~/.openclaw/openclaw.json safely.";
    case "debug":
      return "Gateway snapshots, events, and manual RPC calls.";
    case "logs":
      return "Live tail of the gateway file logs.";
    default:
      return "";
  }
}
